import { Button, Checkbox } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { AppContext } from '../../../../../AppContext';
import FORM_HEADERS from '../../../../../enums/formHeaders';
import {
  getValuationHistory,
  processCoreLogicLevel1,
  processCoreLogicLevel2,
  processE2ValueLevel1,
  processE2ValueLevel2,
  processIso360,
  processProntoAdjustment,
} from '../../../../../services';
import { OrderReviewContext } from '../../../OrderReviewContext';
import styles from './ValuationCalculation.styles';

const propTypes = {
  classes: PropTypes.object.isRequired,
  formDefinition: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  saveAnswers: PropTypes.func.isRequired,
};

const ValuationCalculation = ({ classes, formDefinition, isReadOnly, saveAnswers }) => {
  const { dispatch: orderReviewDispatch, state, actions } = useContext(OrderReviewContext);
  const { dispatch: appDispatch } = useContext(AppContext);
  const [calculatingValuation, setCalculatingValuation] = useState(false);

  const showSnackbar = (message, variant) => {
    appDispatch({
      type: 'SET_SNACKBAR',
      snackbar: {
        isOpen: true,
        message: message,
        variant: variant,
      },
    });
  };

  const saveValuationAnswers = valuationResponse => {
    const valuationAnswers = [];
    valuationResponse
      .filter(response => response.answerText)
      .forEach(response => {
        valuationAnswers.push({
          orderFormHeaderId: formDefinition.orderFormHeaderId,
          formDefinitionId: response.formDefinitionId,
          questionId: response.questionId,
          formQaAnswerId: null,
          type: 'TextBox',
          valueId: null,
          value: null,
          text: response.answerText,
          modifiedOn: new Date(),
          images: [],
          default: false,
        });
      });
    if (valuationAnswers.length > 0) {
      saveAnswers(valuationAnswers);
      valuationAnswers.forEach(answer => {
        orderReviewDispatch({
          type: 'UPDATE_ANSWERS',
          questionId: answer.questionId,
          newAnswers: [answer],
          orderFormHeaderId: formDefinition.orderFormHeaderId,
        });
      });
    }
    setCalculatingValuation(false);
    showSnackbar('Valuation calculation was successful!', 'success');
  };

  const updateValuationHistory = async () => {
    try {
      const valuationHistory = await getValuationHistory(state.orderLineId);
      orderReviewDispatch({
        type: 'UPDATE_VALUATION_HISTORY',
        valuationHistory,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const runValueCalculation = valuationProcess => {
    valuationProcess(state.orderLineId)
      .then(response => {
        console.log(response)
        if (response.return) {
          /* 
          orderReviewDispatch({
            type: 'UPDATE_IS_VALUATION_COMPLETE',
            isValuationComplete: true,
          });
          */

          actions.setIsValuationComplete(true);
          actions.setIsValuationOverride(false);
          saveValuationAnswers(response.return);
        } else if (response.errorMessage) {
          /* 
          orderReviewDispatch({
            type: 'UPDATE_IS_VALUATION_COMPLETE',
            isValuationComplete: false,
          });
          */

          actions.setIsValuationComplete(true);
          actions.setIsValuationOverride(false);
          showSnackbar(`${response?.errorMessage ?? "An unexpected error has occurred in processing please contact support for additinoal information"}`, 'error');
          setCalculatingValuation(false);
        }
        updateValuationHistory();
      })
      .catch(response => {
        let errorMessage = 'An error occurred. Please try again.';
        if (response.errorMessage) {
          errorMessage = response.errorMessage;
        } else if (response.Message) {
          errorMessage = response.Message;
        } else {
          errorMessage = response;
        }
        showSnackbar(errorMessage, 'error');
        setCalculatingValuation(false);
      });
  };

  const handleValuationCalculation = () => {
    setCalculatingValuation(true);
    const activeFormHeaderId = state.formHeaders[state.activeFormHeaderIndex].formHeaderId;
    switch (activeFormHeaderId) {
      case FORM_HEADERS.E2V_LEVEL_1:
      case FORM_HEADERS.E2V_LEVEL_1_OTHER_STRUCTURES_REPLACEMENT_COST:
      case FORM_HEADERS.CIC_RCT_PERSONAL_LINES_RESIDENTIAL_FORM:
      case FORM_HEADERS.CIC_RCT_PERSONAL_LINES_RESIDENTIAL_FORM_VIRTUAL:
      case FORM_HEADERS.CIC_RCT_PERSONAL_LINES_RESIDENTIAL_FORM_EXTERIOR:
      case FORM_HEADERS.CIC_RCT_PERSONAL_LINES_RESIDENTIAL_FORM_EXTERIOR_B:
      case FORM_HEADERS.CIC_RCT_PERSONAL_LINES_VENDOR_HSR:
      case FORM_HEADERS.CIC_RCT_PERSONAL_LINES_VENDOR_HSR_EXTERIOR:
      case FORM_HEADERS.CIC_RCT_PERSONAL_LINES_VENDOR_HSR_VIRTUAL:
      case FORM_HEADERS.MOBILE_HOME:
      case FORM_HEADERS.MOBILE_HOME1:
      case FORM_HEADERS.CIC_RCT_PERSONAL_LINES_EXTERIOR_RESIDENTIAL_FORM:
      case FORM_HEADERS.CIC_RCT_PERSONAL_LINES_VIRTUAL_RESIDENTIAL_FORM:
        runValueCalculation(processE2ValueLevel1);
        return;
      case FORM_HEADERS.L2_E2V_REPLACEMENT_COST:
        runValueCalculation(processE2ValueLevel2);
        return;
      case FORM_HEADERS.QUICKVUE:
        runValueCalculation(processProntoAdjustment);
        return;
      case FORM_HEADERS.CORE_LOGIC_LEVEL_1:
        runValueCalculation(processCoreLogicLevel1);
        return;
      case FORM_HEADERS.L2_CORE_LOGIC_REPLACEMENT_COST:
        runValueCalculation(processCoreLogicLevel2);
        return;
      case FORM_HEADERS.ISO_360_LEVEL_1:
        runValueCalculation(processIso360);
        return;
      default:
        console.log('This form does not have a valuation calculation.');
    }
  };

  const handleValuationOverride = event => {
    const checked = event.target.checked;
    actions.setIsValuationComplete(checked);
    actions.setIsValuationOverride(checked);
  };

  return state.isFieldRep ? null : (
    <React.Fragment>
      <Button
        className={classes.valuationButton}
        variant="contained"
        color="primary"
        disabled={Boolean(isReadOnly || calculatingValuation || state.isAutoSaving || state.isEditing)}
        onClick={handleValuationCalculation}
      >
        {calculatingValuation ? 'Calculating Valuation...' : 'Calculate Valuation'}
      </Button>
      <br />
      <label>
        <Checkbox
          onChange={handleValuationOverride}
          checked={state.isValuationComplete}
          disabled={Boolean(isReadOnly || calculatingValuation || state.isAutoSaving || state.isEditing)}
        ></Checkbox>
        Complete Without Calculation
      </label>
    </React.Fragment>
  );
};

ValuationCalculation.propTypes = propTypes;
export default withStyles(styles)(ValuationCalculation);
